import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API_URL from '../config';

function Subscribe({ token, sub, onSubUpdate, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const checkoutSuccess = searchParams.get('checkout') === 'success';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [polling] = useState(checkoutSuccess);

  // If already subscribed, skip straight to dashboard
  useEffect(() => {
    if (sub?.tier === 'standard') {
      navigate('/dashboard', { replace: true });
    }
  }, [sub, navigate]);

  // After Stripe checkout success: poll until subscription is confirmed
  useEffect(() => {
    if (!polling) return;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`${API_URL}/api/protected/subscription`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.tier === 'standard') {
            clearInterval(interval);
            onSubUpdate(data);
            navigate('/dashboard', { replace: true });
            return;
          }
        }
      } catch {}
      if (attempts >= 15) {
        clearInterval(interval);
        // Optimistically allow through — webhook may still be in flight
        onSubUpdate({ tier: 'standard', status: 'trialing' });
        navigate('/dashboard', { replace: true });
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [polling, token, navigate, onSubUpdate]);

  const handleStartTrial = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/protected/subscription/checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('Failed to create checkout session');
      const data = await res.json();
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  if (polling) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
          <svg width="36" height="36" viewBox="0 0 32 32" fill="none" style={{ borderRadius: 8, marginBottom: 16 }}>
            <rect width="32" height="32" rx="8" fill="#3b82f6"/>
            <path d="M6 16c0 0 4-6.5 10-6.5S26 16 26 16s-4 6.5-10 6.5S6 16 6 16z" stroke="white" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
            <circle cx="16" cy="16" r="2.5" fill="white"/>
          </svg>
          <p style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 8 }}>Setting up your account…</p>
          <p style={{ fontSize: 14 }}>This takes just a moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)' }}>
      <div className="card" style={{ width: '100%', maxWidth: '440px', padding: '40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28, justifyContent: 'center' }}>
          <svg width="28" height="28" viewBox="0 0 32 32" fill="none" style={{ borderRadius: 8, flexShrink: 0 }}>
            <rect width="32" height="32" rx="8" fill="#3b82f6"/>
            <path d="M6 16c0 0 4-6.5 10-6.5S26 16 26 16s-4 6.5-10 6.5S6 16 6 16z" stroke="white" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
            <circle cx="16" cy="16" r="2.5" fill="white"/>
          </svg>
          <span style={{ fontWeight: 700, fontSize: 17, color: 'var(--text-primary)' }}>Agent Oversight</span>
        </div>

        <h2 style={{ fontSize: 22, marginBottom: 8, color: 'var(--text-primary)', textAlign: 'center' }}>Start your free trial</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 28, fontSize: 15, textAlign: 'center' }}>
          7 days free, then $5/month. Cancel anytime.
        </p>

        <div style={{ marginBottom: 28, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            'Unlimited agent monitoring',
            'Real-time traces & logs',
            'API Key Vault with AES-256 encryption',
            'Quality metrics & governance controls',
          ].map(f => (
            <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: 'var(--text-secondary)' }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" fill="rgba(34,197,94,0.15)"/>
                <path d="M5 8l2 2 4-4" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {f}
            </div>
          ))}
        </div>

        {error && <div className="alert error" style={{ marginBottom: 16 }}>{error}</div>}

        <button
          className="btn-primary"
          onClick={handleStartTrial}
          disabled={loading}
          style={{ width: '100%', fontSize: 15, padding: '12px 0' }}
        >
          {loading ? 'Redirecting to Stripe…' : 'Start 7-day free trial →'}
        </button>

        <p style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
          Card required. You won't be charged until day 8. Cancel anytime.
        </p>

        <button
          onClick={onLogout}
          style={{ display: 'block', margin: '20px auto 0', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer' }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

export default Subscribe;
